import React, {
    useContext,
    useRef,
    useState,
    useEffect,
    useCallback,
} from 'react';

const CommunityContentContext = React.createContext(undefined);

const CommunityContentProvider = ({ children, content, recentContent }) => {
    const [windowWidth, setWindowWidth] = useState();
    const [backgroundHeight, setBackgroundHeight] = useState(0);
    const [imageHeight, setImageHeight] = useState(0);
    const topConvincerRef = useRef();
    const articlePictureRef = useRef();

    useEffect(() => {
        if (imageHeight > 0) {
            return;
        }
        let retries = 15;
        let success = false;
        const pollFunction = () => {
            retries -= 1;
            const imgChildren = articlePictureRef?.current?.querySelectorAll('img');
            const imgChild = imgChildren?.[0];
            const height = imgChild?.clientHeight;
            if (height > 0) {
                setImageHeight(height);
                success = true;
            }
            if (retries > 0 || !success) {
                setTimeout(pollFunction, 10);
            }
        };
        pollFunction();
    }, [imageHeight, articlePictureRef]);

    useEffect(() => {
        if (imageHeight === 0) {
            return;
        }

        if (topConvincerRef?.current && articlePictureRef?.current) {
            if (windowWidth < 904) {
                const offsetHeight = articlePictureRef?.current.offsetTop;
                if (offsetHeight && imageHeight) {
                    setBackgroundHeight(offsetHeight + imageHeight - 46);
                }
                return;
            }

            const rect = topConvincerRef?.current?.getBoundingClientRect();
            const { height } = rect;
            const offsetHeight = topConvincerRef?.current.offsetTop;
            if (offsetHeight && height) {
                setBackgroundHeight(offsetHeight + height + 32);
            }
        }
    }, [
        topConvincerRef,
        windowWidth,
        articlePictureRef,
        // isLoggedIn,
        imageHeight,
    ]);
    useEffect(() => {
        setWindowWidth(window.innerWidth);
    }, []);

    const windowResize = useCallback(() => {
        // eslint-disable-next-line no-underscore-dangle
        const _windowWidth = window?.innerWidth;
        if (_windowWidth && windowWidth !== _windowWidth) {
            setWindowWidth(_windowWidth);
        }
        const imgChildren = articlePictureRef.current?.querySelectorAll('img');
        if (imgChildren?.length) {
            const imgChild = imgChildren[0];
            const height = imgChild.clientHeight;
            setImageHeight(height);
        }
    }, [windowWidth]);

    useEffect(() => {
        windowResize();
        window?.addEventListener('resize', windowResize, { passive: true });
        return () => {
            window?.removeEventListener('resize', windowResize);
        };
    }, [windowResize]);

    const {
        paidCompany,
        author,
        contentModifiedDate,
        hash,
        type,
        streamDate,
        contentDate,
        lastId,
        commentCount,
        comments,
    } = content;

    let contentTitle;
    let contentImage;
    let contentBody;
    let contentDescription;
    let isSponsor;
    let contentType;
    const replaceRegex = /(.jpg|.png)/gim;
    const replaceimgRegex = /(<img )/gim;
    if (content?.type === 'community-podcast') {
        const { podcast } = content;
        contentTitle = podcast?.contentTitle;
        contentImage = podcast?.contentImage;
        contentBody = podcast?.contentBody
            .replace(replaceRegex, '.webp')
            .replace(
                replaceimgRegex,
                '<img loading="lazy" fetchpriority="low" '
            );
        contentDescription = podcast?.contentDescription;
        isSponsor = podcast?.isSponsor;
        contentType = 'podcasts';
    }
    if (content?.type === 'editorial-content') {
        const { article } = content;
        contentTitle = article?.contentTitle;
        contentImage = article?.contentImage;
        contentBody = article?.contentBody
            .replace(replaceRegex, '.webp')
            .replace(
                replaceimgRegex,
                '<img loading="lazy" fetchpriority="low" '
            );
        contentDescription = article?.description;
        isSponsor = article?.isSponsor;
        contentType = 'articles';
    }

    const editorRef = useRef();

    let seeMoreLink = '/advice';
    if (contentType === 'podcasts') {
        seeMoreLink = '/community-podcasts';
    }

    return (
        <CommunityContentContext.Provider
            value={{
                content,
                topConvincerRef,
                articlePictureRef,
                backgroundHeight,
                editorRef,
                contentTitle,
                contentImage,
                contentBody,
                contentDescription,
                paidCompany,
                lastId,
                author,
                contentModifiedDate:
                    contentModifiedDate || streamDate || contentDate,
                hash,
                type,
                windowWidth,
                windowResize,
                isSponsor,
                commentCount,
                comments,
                recentContent: recentContent || [],
                contentType,
                seeMoreLink,
                audioSrc: content?.podcast?.audioSrc,
            }}
        >
            {children}
        </CommunityContentContext.Provider>
    );
};

const useCommunityContent = () => {
    const context = useContext(CommunityContentContext);
    if (context === undefined) {
        throw new Error(
            'useCommunityContent can only be used inside CommunityContentProvider'
        );
    }
    return context;
};

export { CommunityContentProvider, useCommunityContent };
